/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from '../../../components/DatePicker/Picker';
import { sendRequest } from '../../../utils/dateHelpers';
import {
  EmptyState as SharedEmptyState,
  SegmentedControl
} from '../../../components/ui';

const USAGE_HISTORY_URL = '/billing/usage-history';
const DAY_MS = 24 * 60 * 60 * 1000;

const formatTokens = value => {
  if (value == null) return 'Custom';
  return new Intl.NumberFormat('en-US', {
    notation: Number(value) >= 1_000_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1
  }).format(Number(value));
};

const formatExactTokens = value =>
  new Intl.NumberFormat('en-US').format(Number(value || 0));

const formatCurrency = cents =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Number(cents || 0) / 100);

const formatDate = value => {
  if (!value) return 'Not available';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const toIsoDate = date => date.toISOString().slice(0, 10);

export const buildUsageRows = (
  dailyUsage,
  startDate,
  endDate,
  includedTokens,
  showAllowanceStatus
) => {
  if (!startDate || !endDate) return [];
  const byDate = new Map(
    (dailyUsage || []).map(item => [item.date, Number(item.tokens || 0)])
  );
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  const rows = [];
  let cumulative = 0;

  for (
    let cursor = start;
    cursor <= end;
    cursor = new Date(cursor.getTime() + DAY_MS)
  ) {
    const date = toIsoDate(cursor);
    const tokens = byDate.get(date) || 0;
    cumulative += tokens;
    rows.push({
      date,
      tokens,
      cumulative_tokens: cumulative,
      status:
        showAllowanceStatus && includedTokens != null
          ? cumulative > Number(includedTokens)
            ? 'Overage'
            : 'Included'
          : 'Usage'
    });
  }
  return rows;
};

const MetricCard = ({ label, value, detail, tone = 'cyan' }) => (
  <div className="rounded-[1.5rem] border border-slate-200/80 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] dark:border-slate-800 dark:bg-slate-900">
    <div
      className={`mb-4 flex h-10 w-10 items-center justify-center rounded-2xl text-lg ${
        tone === 'amber'
          ? 'bg-amber-50 text-amber-600 dark:bg-amber-950'
          : tone === 'violet'
            ? 'bg-violet-50 text-violet-600 dark:bg-violet-950'
            : 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950'
      }`}
    >
      {tone === 'amber' ? '↗' : tone === 'violet' ? '$' : '●'}
    </div>
    <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
      {label}
    </p>
    <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white">
      {value}
    </p>
    <p className="mt-1 text-xs text-slate-500">{detail}</p>
  </div>
);

const UsageHistorySkeleton = () => (
  <div
    className="min-h-screen space-y-6 bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8"
    role="status"
    aria-label="Loading usage history"
  >
    <div className="mx-auto max-w-[1440px] space-y-6">
      <div className="rounded-[2rem] border border-slate-200/80 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-5 w-32 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="mt-5 h-10 w-72 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="mt-4 h-4 w-full max-w-2xl animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="grid gap-5 lg:grid-cols-4">
        {[1, 2, 3, 4].map(item => (
          <div
            key={item}
            className="h-36 animate-pulse rounded-[1.5rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          />
        ))}
      </div>
      <div className="h-[420px] animate-pulse rounded-[2rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900" />
    </div>
  </div>
);

const PaymentSummary = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePreset, setActivePreset] = useState('cycle');
  const [dateRange, setDateRange] = useState(null);

  const fetchUsageHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = dateRange
        ? `?startDate=${encodeURIComponent(
            dateRange.startDate
          )}&endDate=${encodeURIComponent(dateRange.endDate)}`
        : '';
      const response = await sendRequest(`${USAGE_HISTORY_URL}${query}`, 'GET');
      setHistory(response);
    } catch (requestError) {
      setError(requestError.message || 'Usage history could not be loaded.');
      toast.error(requestError.message || 'Usage history could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchUsageHistory();
  }, [fetchUsageHistory]);

  const applyPreset = (preset, days) => {
    setActivePreset(preset);
    if (!days) {
      setDateRange(null);
      return;
    }
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * DAY_MS);
    setDateRange({
      startDate: toIsoDate(start),
      endDate: toIsoDate(end)
    });
  };

  const handleCustomRange = range => {
    setActivePreset('custom');
    setDateRange(range);
  };

  const cycle = history?.current_cycle || null;
  const range = history?.range || null;
  const isOverage = Number(cycle?.overage_tokens || 0) > 0;
  const usagePercent = cycle?.usage_percent;
  const progressWidth = Math.min(Math.max(Number(usagePercent || 0), 0), 100);
  const usageRows = useMemo(
    () =>
      buildUsageRows(
        history?.daily_usage,
        range?.start_date,
        range?.end_date,
        cycle?.included_tokens,
        activePreset === 'cycle'
      ),
    [
      activePreset,
      cycle?.included_tokens,
      history?.daily_usage,
      range?.end_date,
      range?.start_date
    ]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        backgroundColor: 'transparent',
        height: 390,
        spacing: [16, 8, 8, 8]
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: usageRows.map(row => row.date),
        lineColor: '#cbd5e1',
        tickColor: '#cbd5e1',
        labels: {
          style: { color: '#64748b', fontSize: '11px' },
          formatter() {
            return new Date(`${this.value}T00:00:00.000Z`).toLocaleDateString(
              'en-US',
              { month: 'short', day: 'numeric', timeZone: 'UTC' }
            );
          }
        }
      },
      yAxis: [
        {
          min: 0,
          title: { text: 'Daily tokens', style: { color: '#64748b' } },
          gridLineColor: '#e2e8f0',
          labels: {
            style: { color: '#64748b' },
            formatter() {
              return formatTokens(this.value);
            }
          }
        },
        {
          min: 0,
          opposite: true,
          title: { text: 'Cumulative', style: { color: '#64748b' } },
          gridLineWidth: 0,
          labels: {
            style: { color: '#64748b' },
            formatter() {
              return formatTokens(this.value);
            }
          },
          plotLines:
            activePreset === 'cycle' && cycle?.included_tokens != null
              ? [
                  {
                    value: Number(cycle.included_tokens),
                    color: '#f59e0b',
                    dashStyle: 'Dash',
                    width: 2,
                    label: {
                      text: 'Plan allowance',
                      style: { color: '#d97706', fontWeight: '700' }
                    }
                  }
                ]
              : []
        }
      ],
      legend: {
        align: 'left',
        itemStyle: { color: '#475569', fontWeight: '600' }
      },
      tooltip: {
        shared: true,
        borderRadius: 14,
        formatter() {
          const row = usageRows[this.points?.[0]?.point?.index];
          return [
            `<b>${formatDate(row?.date)}</b>`,
            `Daily: <b>${formatExactTokens(row?.tokens)} tokens</b>`,
            `Cumulative: <b>${formatExactTokens(
              row?.cumulative_tokens
            )} tokens</b>`
          ].join('<br/>');
        }
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 5,
          color: '#06b6d4',
          maxPointWidth: 26
        },
        spline: {
          color: '#4f46e5',
          lineWidth: 3,
          marker: { enabled: false }
        },
        series: { animation: { duration: 500 } }
      },
      series: [
        {
          type: 'column',
          name: 'Daily usage',
          data: usageRows.map(row => row.tokens)
        },
        {
          type: 'spline',
          name: 'Cumulative usage',
          yAxis: 1,
          data: usageRows.map(row => row.cumulative_tokens)
        }
      ],
      responsive: {
        rules: [
          {
            condition: { maxWidth: 640 },
            chartOptions: {
              chart: { height: 320 },
              yAxis: [{ title: { text: null } }, { title: { text: null } }]
            }
          }
        ]
      }
    }),
    [activePreset, cycle?.included_tokens, usageRows]
  );

  const handleExport = () => {
    if (!usageRows.length) {
      toast.error('No usage data to export.');
      return;
    }
    const lines = [
      ['Date', 'Tokens', 'Cumulative Tokens', 'Status'],
      ...usageRows.map(row => [
        row.date,
        row.tokens,
        row.cumulative_tokens,
        row.status
      ])
    ];
    const csv = lines
      .map(line => line.map(value => `"${value}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `gitme-token-usage-${range.start_date}-to-${range.end_date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !history) {
    return <UsageHistorySkeleton />;
  }

  if (error && !history) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-[1.5rem] border border-rose-200 bg-rose-50 p-8 text-center">
        <h2 className="text-xl font-black text-rose-900">
          Usage history is unavailable
        </h2>
        <p className="mt-2 text-sm text-rose-700">{error}</p>
        <button
          type="button"
          onClick={fetchUsageHistory}
          className="mt-5 rounded-xl bg-rose-700 px-5 py-3 font-bold text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-8">
        <section className="relative isolate overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-600 px-6 py-8 text-white shadow-sm sm:px-10 dark:border-slate-700">
          <div className="absolute -right-20 -top-32 -z-10 h-96 w-96 rounded-full bg-cyan-400/25 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 -z-10 h-96 w-96 rounded-full bg-blue-600/30 blur-3xl" />
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-cyan-100">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-200 shadow-[0_0_12px_#67e8f9]" />
                Token usage
              </div>
              <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-5xl">
                Usage history
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-white/85 sm:text-lg">
                Follow daily token consumption, your current plan allowance and
                every overage charge without legacy balance or cost metrics.
              </p>
            </div>
            <Link
              to="/settings/plan-configuration"
              className="inline-flex items-center justify-center rounded-2xl bg-cyan-400 px-5 py-3.5 font-black text-slate-950 transition hover:-translate-y-0.5 hover:bg-cyan-300"
            >
              Manage plan
            </Link>
          </div>
        </section>

        {cycle && (
          <section className="rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-900 sm:p-8">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-cyan-600 dark:text-cyan-300">
                  {cycle.plan_name || 'Current plan'} · Current cycle
                </p>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-5xl font-black tracking-tight text-slate-950 dark:text-white">
                    {usagePercent == null
                      ? 'Custom'
                      : `${usagePercent.toFixed(1)}%`}
                  </span>
                  <span className="text-sm font-bold text-slate-400">used</span>
                </div>
                <p className="mt-3 text-sm text-slate-500">
                  {formatDate(cycle.period_start)} –{' '}
                  {formatDate(cycle.period_end)}
                </p>
                <div className="mt-6 h-4 overflow-hidden rounded-full bg-slate-100 p-0.5 dark:bg-slate-800">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isOverage
                        ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                        : 'bg-gradient-to-r from-cyan-400 to-blue-600'
                    }`}
                    style={{ width: `${progressWidth}%` }}
                  />
                </div>
                <div className="mt-3 flex justify-between text-xs font-bold text-slate-500">
                  <span>{formatTokens(cycle.used_tokens)} used</span>
                  <span>
                    {isOverage
                      ? `${formatTokens(cycle.overage_tokens)} over plan`
                      : `${formatTokens(cycle.remaining_tokens)} remaining`}
                  </span>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <MetricCard
                  label="Plan allowance"
                  value={formatTokens(cycle.included_tokens)}
                  detail={`${formatTokens(cycle.used_tokens)} consumed this cycle`}
                />
                <MetricCard
                  label={isOverage ? 'Overage tokens' : 'Tokens remaining'}
                  value={formatTokens(
                    isOverage ? cycle.overage_tokens : cycle.remaining_tokens
                  )}
                  detail={
                    isOverage
                      ? 'Usage beyond the included allowance'
                      : 'Available before overage'
                  }
                  tone={isOverage ? 'amber' : 'cyan'}
                />
                <MetricCard
                  label="Paid overage charges"
                  value={`${cycle.paid_charge_count || 0}`}
                  detail={`${cycle.paid_units || 0} additional 1M units`}
                  tone="violet"
                />
                <MetricCard
                  label="Overage paid"
                  value={formatCurrency(cycle.paid_amount_cents)}
                  detail={`${history?.overage_summary?.pending_charge_count || 0} pending · ${
                    history?.overage_summary?.failed_charge_count || 0
                  } failed`}
                  tone="violet"
                />
                {cycle.overage_collection_mode === 'grant_v1' && (
                  <>
                    <MetricCard
                      label="Additional capacity opened"
                      value={formatTokens(cycle.granted_overage_tokens)}
                      detail="No upfront charge for opened capacity"
                    />
                    <MetricCard
                      label="Remaining added capacity"
                      value={formatTokens(cycle.grant_remaining_tokens)}
                      detail="Expires at the end of this cycle"
                    />
                    <MetricCard
                      label="Unpaid overage"
                      value={formatCurrency(cycle.unpaid_overage_amount_cents)}
                      detail="Only started 1M units actually used"
                      tone="amber"
                    />
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="rounded-[2rem] border border-slate-200/80 bg-white shadow-[0_16px_45px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-100 p-6 dark:border-slate-800">
            <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-cyan-600 dark:text-cyan-300">
                  Daily consumption
                </p>
                <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
                  {formatTokens(range?.total_tokens)} tokens in selected range
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {formatDate(range?.start_date)} –{' '}
                  {formatDate(range?.end_date)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <SegmentedControl
                  items={[
                    { value: 'cycle', label: 'Current cycle' },
                    { value: '30', label: '30 days' },
                    { value: '90', label: '90 days' }
                  ]}
                  value={activePreset}
                  onChange={value =>
                    applyPreset(value, value === 'cycle' ? null : Number(value))
                  }
                />
                <DatePicker
                  className="shrink-0"
                  dateRange={
                    dateRange || {
                      startDate: range?.start_date,
                      endDate: range?.end_date
                    }
                  }
                  onChange={handleCustomRange}
                  direction="down"
                />
                <button
                  type="button"
                  onClick={handleExport}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:border-cyan-400 hover:text-cyan-700 dark:border-slate-700 dark:text-slate-300"
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            {usageRows.some(row => row.tokens > 0) ? (
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            ) : (
              <SharedEmptyState
                title="No token usage in this range"
                description="Choose another date range or return after new analyses have been processed."
              />
            )}
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200/80 bg-white shadow-[0_16px_45px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-100 p-6 dark:border-slate-800">
            <h2 className="text-xl font-black text-slate-950 dark:text-white">
              Daily usage
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Exact token consumption by UTC date.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-400 dark:bg-slate-950">
                <tr>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Tokens used</th>
                  <th className="px-6 py-4">Cumulative</th>
                  <th className="px-6 py-4">Usage status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {usageRows.map(row => (
                  <tr key={row.date}>
                    <td className="px-6 py-4 text-sm font-bold text-slate-800 dark:text-slate-200">
                      {formatDate(row.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {formatExactTokens(row.tokens)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {formatExactTokens(row.cumulative_tokens)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-black ${
                          row.status === 'Overage'
                            ? 'bg-amber-100 text-amber-800'
                            : row.status === 'Included'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-8 xl:grid-cols-2">
          <section className="rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-xl font-black text-slate-950 dark:text-white">
              Billing cycles
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Plan capacity and usage for periods in the selected range.
            </p>
            <div className="mt-5 space-y-3">
              {(history?.cycles || []).length ? (
                history.cycles.map(item => (
                  <div
                    key={item.cycle_id}
                    className="rounded-2xl border border-slate-100 p-4 dark:border-slate-800"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-black text-slate-900 dark:text-white">
                          {item.plan_name || `Plan ${item.plan_id}`}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {formatDate(item.period_start)} –{' '}
                          {formatDate(item.period_end)}
                        </p>
                      </div>
                      <span className="text-sm font-black text-cyan-600 dark:text-cyan-300">
                        {item.usage_percent == null
                          ? 'Custom'
                          : `${item.usage_percent.toFixed(1)}%`}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold text-slate-500">
                      <span>{formatTokens(item.used_tokens)} used</span>
                      <span>{formatTokens(item.included_tokens)} included</span>
                      <span>{item.paid_units || 0} paid overage units</span>
                    </div>
                  </div>
                ))
              ) : (
                <SharedEmptyState
                  title="No billing cycles"
                  description="No Pricing V2 billing cycle overlaps this date range."
                />
              )}
            </div>
          </section>

          <section className="rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-xl font-black text-slate-950 dark:text-white">
              Overage charges
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Paddle charges created after crossing the included allowance.
            </p>
            <div className="mt-5 space-y-3">
              {(history?.overage_charges || []).length ? (
                history.overage_charges.map((charge, index) => (
                  <div
                    key={`${charge.transaction_id || 'charge'}-${index}`}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4 dark:border-slate-800"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-black text-slate-900 dark:text-white">
                          {formatCurrency(charge.amount_cents)}
                        </p>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${
                            charge.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-800'
                              : charge.status === 'failed'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {charge.status}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatDate(charge.charged_at)}
                      </p>
                    </div>
                    <div className="text-right text-sm font-bold text-slate-600 dark:text-slate-300">
                      <p>{charge.unit_count} × 1M units</p>
                      <p className="mt-1 text-xs text-slate-400">
                        {formatTokens(charge.token_count)} tokens
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <SharedEmptyState
                  title="No overage charges"
                  description="This workspace has not generated an overage charge in the selected range."
                />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
